import React, { useState } from 'react'; 
import {
  AppBar,
  Box,
  Stack,
  Toolbar,
  IconButton,
  TextField,
  InputAdornment,
  Typography
} from '@mui/material';
import {
  Menu as MenuIcon,
  Fullscreen as FullscreenIcon,
  DarkMode as DarkModeIcon,
  LightMode as LightModeIcon,
  Search as SearchIcon
} from '@mui/icons-material';
import { useNavigate, useLocation } from 'react-router-dom';
import { useSelector } from 'react-redux';
import NotificationDropdown from "../CommonForBoth/TopbarDropdown/NotificationDropdown";
import ProfileMenu from '../CommonForBoth/TopbarDropdown/ProfileMenu';

export const Navbar = ({
  showSideBarOption,
  isCollapsed,
  toggleSidebar,
  mode,
  toggleMode,
  isMobile
}) => {
  const navigate = useNavigate();
  const location = useLocation();
  
  // Import logos
  const logodark = require('../../assets/images/logo.png');
  const darkmodemodelogo = require('../../assets/images/Dark_mode_logo.png');
  const organization = useSelector((state) => state.organization);

  const [searchOptions, setSearchOptions] = useState([]);
  const [isSearchOpen, setIsSearchOpen] = useState(false);

  const toggleFullscreen = () => {
    if (!document.fullscreenElement) {
      document.documentElement.requestFullscreen().catch(console.error);
    } else {
      document.exitFullscreen();
    }
  };

  // flattening logic unchanged…
  const flattenedOptions = organization?.flatMap(cat =>
    cat.options?.flatMap(opt => [
      { label: opt.Label, to: opt.to || '', id: opt.id?.trim() || '' },
      ...(opt.menu?.flatMap(menu => [
        { label: menu.Label, to: menu.to || '', id: menu.id?.trim() || '' },
        ...(menu.children?.map(c => ({ label: c.Label, to: c.to || '', id: c.id?.trim() || '' })) || [])
      ]) || [])
    ]) || []
  ) || [];

  const handleInputChange = (_, value) => {
    if (value?.length >= 3) {
      setSearchOptions(flattenedOptions.filter(o =>
        o.label.toLowerCase().includes(value.toLowerCase())
      ));
    } else {
      setSearchOptions([]);
    }
  };

  const getActiveLabel = () => {
    const curPath = location.pathname;
    let activeLabel = "";
    let maxMatchLen = 0;
    
    flattenedOptions.forEach(opt => {
      if (opt.to && opt.to !== "/") {
        if (curPath === opt.to || curPath.startsWith(opt.to + "/")) {
          if (opt.to.length >= maxMatchLen) {
            activeLabel = opt.label;
            maxMatchLen = opt.to.length;
          }
        }
      } else if (opt.to === curPath) {
        if (opt.to.length >= maxMatchLen) {
          activeLabel = opt.label;
          maxMatchLen = opt.to.length;
        }
      }
    });
    return activeLabel;
  };

  const currentTitle = getActiveLabel();

  return (
    <AppBar
      component="nav"
      position="static"
      sx={{
        boxShadow: 'none',
        height: '64px',
        background: 'var(--canvas)',
        borderBottom: '1px solid var(--hairline)',
        zIndex: 1000,
      }}
    >
      <Toolbar sx={{ justifyContent: 'space-between', height: '64px', minHeight: '64px !important', paddingLeft: isCollapsed ? '24px' : '24px' }}>
        <Box sx={{ display: 'flex', alignItems: 'center' }}>
          {isMobile && showSideBarOption && (
            <IconButton 
              onClick={toggleSidebar}
              sx={{ mr: 1, color: mode === 'dark' ? '#fff' : 'var(--vault-navy)' }}
            >
              <MenuIcon />
            </IconButton>
          )}
          {showSideBarOption && !isMobile && (
            <Box 
              sx={{ 
                width: isCollapsed ? '140px' : '0px', 
                opacity: isCollapsed ? 1 : 0,
                overflow: 'hidden', 
                transition: 'all 0.3s ease',
                display: 'flex',
                alignItems: 'center',
                marginRight: isCollapsed ? '24px' : '0'
              }}
            >
              <img 
                src={mode === 'dark' ? darkmodemodelogo : logodark} 
                alt="Logo" 
                style={{ width: '100%', maxWidth: '140px', minWidth: '140px' }} 
              />
            </Box>
          )}
          
          <Typography 
            variant="h5" 
            sx={{ 
              ml: (showSideBarOption && !isMobile && !isCollapsed) ? 0 : 2, 
              color: mode === 'dark' ? '#fff' : 'var(--vault-navy)',
              fontWeight: 700,
              fontSize: '1.5rem',
              letterSpacing: '0.5px',
              display: currentTitle ? 'block' : 'none'
            }}
          >
            {currentTitle}
          </Typography>
        </Box>

        <Box sx={{ display: 'flex', alignItems: 'center' }}>
          <Stack direction="row" spacing={1} alignItems="center">

            <Box textAlign="center">
              <IconButton onClick={toggleFullscreen} sx={{ color: 'var(--vault-navy)' }}>
                <FullscreenIcon />
              </IconButton>
            </Box>

            { <Box textAlign="center">
            <IconButton onClick={toggleMode} sx={{ color: 'var(--vault-navy)' }}>
  {mode === 'dark' ? <LightModeIcon /> : <DarkModeIcon />}
</IconButton>

              <Typography variant="caption" sx={{ color: 'var(--vault-navy)', mt: -1 }}>
                {mode === 'dark' ? 'Light' : 'Dark'}
              </Typography>
            </Box> }

            {/* <IconButton color="inherit">
              <NotificationDropdown />
            </IconButton> */}

            {/* 
              We want only the avatar to show (no username text). 
              The ProfileMenu component is left intact, but wrapped in a container that hides
              likely username elements (common selectors). This preserves the dropdown functionality
              while showing only the picture. If ProfileMenu uses different internal classNames,
              you can update the selectors below inside sx.
            */}
            <Box
              sx={{
                // attempt to hide name/label inside ProfileMenu while keeping avatar & dropdown
                '& .profile-name': { display: 'none !important' }, // common custom class
                '& .MuiTypography-root': { display: 'none !important' }, // MUI typography inside
                '& .username': { display: 'none !important' }, // alternate common class
                // ensure avatar (img or avatar) remains visible
                '& img, & .MuiAvatar-root, & svg': { display: 'inline-block !important' },
              }}
            >
              <ProfileMenu />
            </Box>

          </Stack>
        </Box>
      </Toolbar>

      {isSearchOpen && (
        <Box
          sx={{
            width: '100%',
            position: 'absolute',
            top: 70,
            left: 0,
            right: 0,
            backgroundColor: mode === 'dark' ? '#252b3b' : 'white',
            p: 4,
          }}
        >
          <TextField
            fullWidth
            placeholder="Search..."
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon />
                </InputAdornment>
              ),
            }}
            onChange={e => handleInputChange(e, e.target.value)}
          />
        </Box>
      )}
    </AppBar>
  );
};
